import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

type OrderStatus = 'all' | 'pending' | 'shipped' | 'delivered';

const mockOrders = [
  {
    id: 'ORD20250411001',
    product: '英雄钢笔礼盒套装 学生练字专用金属笔尖',
    merchant: '英雄钢笔官方旗舰店',
    spec: '标准款',
    quantity: 2,
    price: 68.0,
    shipping: 8.0,
    total: 144.0,
    status: 'shipped',
    trackingNo: 'SF1234567890',
    carrier: '顺丰速运',
    address: '北京市海淀区中关村大街1号 张三 138****8888',
    orderTime: '2025-04-11 09:23',
    shipTime: '2025-04-11 14:30',
    image: 'https://public.readdy.ai/ai/img_res/76b29297a14a97ad16dcb2ab70d62e21.jpg',
    tracking: [
      { time: '2025-04-11 14:30', desc: '包裹已由顺丰速运揽收', location: '北京市朝阳区' },
      { time: '2025-04-11 18:45', desc: '包裹已到达北京转运中心', location: '北京市' },
      { time: '2025-04-12 06:20', desc: '包裹正在运输中', location: '途中' },
    ],
  },
  {
    id: 'ORD20250410002',
    product: '2025高考数学真题全解析+模拟卷套装',
    merchant: '教育出版社官方店',
    spec: '平装版',
    quantity: 1,
    price: 45.0,
    shipping: 0,
    total: 45.0,
    status: 'delivered',
    trackingNo: 'YT9876543210',
    carrier: '圆通速递',
    address: '上海市浦东新区张江高科技园区 李四 139****9999',
    orderTime: '2025-04-10 15:10',
    shipTime: '2025-04-10 17:00',
    image: 'https://readdy.ai/api/search-image?query=Chinese%20high%20school%20math%20exam%20preparation%20book%20set%20with%20practice%20papers%2C%20clean%20white%20background%2C%20educational%20textbook%20product%20photography%2C%20red%20and%20white%20cover%20design&width=80&height=80&seq=o002&orientation=squarish',
    tracking: [
      { time: '2025-04-10 17:00', desc: '包裹已由圆通速递揽收', location: '上海市' },
      { time: '2025-04-11 09:00', desc: '包裹已到达上海转运中心', location: '上海市' },
      { time: '2025-04-11 14:00', desc: '快递员正在派送', location: '上海市浦东新区' },
      { time: '2025-04-11 16:30', desc: '包裹已签收，感谢您的购买！', location: '上海市浦东新区' },
    ],
  },
  {
    id: 'ORD20250409003',
    product: '儿童校服套装（春秋款）男女通用 纯棉透气',
    merchant: '童装优选馆',
    spec: '130cm',
    quantity: 1,
    price: 89.0,
    shipping: 12.0,
    total: 101.0,
    status: 'pending',
    trackingNo: '',
    carrier: '',
    address: '广州市天河区珠江新城 王五 137****7777',
    orderTime: '2025-04-09 20:45',
    shipTime: '',
    image: 'https://readdy.ai/api/search-image?query=Chinese%20children%20school%20uniform%20set%20spring%20autumn%20style%2C%20navy%20blue%20and%20white%2C%20clean%20white%20background%2C%20product%20photography%2C%20neat%20and%20tidy%2C%20cotton%20fabric&width=80&height=80&seq=o003&orientation=squarish',
    tracking: [],
  },
];

const statusConfig: Record<string, { label: string; color: string; icon: string }> = {
  pending: { label: '待发货', color: 'text-amber-600 bg-amber-50', icon: 'ri-time-line' },
  shipped: { label: '运输中', color: 'text-blue-600 bg-blue-50', icon: 'ri-truck-line' },
  delivered: { label: '已签收', color: 'text-green-600 bg-green-50', icon: 'ri-checkbox-circle-line' },
};

export default function OrdersPage() {
  const [activeStatus, setActiveStatus] = useState<OrderStatus>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmedIds, setConfirmedIds] = useState<Set<string>>(new Set());

  const filtered = activeStatus === 'all' ? mockOrders : mockOrders.filter(o => o.status === activeStatus);

  const handleConfirmReceive = (orderId: string) => {
    setConfirmedIds(prev => new Set([...prev, orderId]));
  };

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      <Navbar />
      <div className="pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-700 py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-1">
              <Link to="/shop" className="text-white/70 hover:text-white text-sm cursor-pointer transition-colors">商城</Link>
              <i className="ri-arrow-right-s-line text-white/50"></i>
              <span className="text-white text-sm">我的订单</span>
            </div>
            <h1 className="text-white font-black text-2xl">我的订单</h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
          {/* Status Filter */}
          <div className="flex gap-1 bg-gray-100 rounded-full px-1 py-1 mb-6 w-fit">
            {([
              { id: 'all', label: '全部订单' },
              { id: 'pending', label: '待发货' },
              { id: 'shipped', label: '运输中' },
              { id: 'delivered', label: '已签收' },
            ] as { id: OrderStatus; label: string }[]).map(s => (
              <button
                key={s.id}
                onClick={() => setActiveStatus(s.id)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeStatus === s.id ? 'bg-red-600 text-white' : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Orders List */}
          <div className="flex flex-col gap-4">
            {filtered.map(order => {
              const isConfirmed = confirmedIds.has(order.id);
              const effectiveStatus = isConfirmed ? 'delivered' : order.status;
              const cfg = statusConfig[effectiveStatus];

              return (
                <div key={order.id} className="bg-white rounded-2xl overflow-hidden">
                  {/* Order Header */}
                  <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 font-mono">{order.id}</span>
                      <span className="text-xs text-gray-300">·</span>
                      <span className="text-xs text-gray-400">{order.orderTime}</span>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 ${cfg.color}`}>
                      <i className={cfg.icon}></i>{cfg.label}
                    </span>
                  </div>

                  {/* Product Info */}
                  <div className="flex items-start gap-4 px-5 py-4">
                    <img src={order.image} alt={order.product} className="w-16 h-16 rounded-xl object-cover object-top flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 line-clamp-2">{order.product}</p>
                      <p className="text-xs text-gray-400 mt-1">{order.merchant} · {order.spec} × {order.quantity}</p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-sm font-black text-red-600">¥{order.total.toFixed(1)}</span>
                        {order.shipping > 0 && (
                          <span className="text-xs text-gray-400">含运费 ¥{order.shipping}</span>
                        )}
                        {order.shipping === 0 && (
                          <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">免运费</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Tracking Info */}
                  {order.trackingNo && (
                    <div className="mx-5 mb-4 bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ri-truck-line text-blue-500 text-sm"></i>
                          <span className="text-xs font-semibold text-gray-700">{order.carrier}</span>
                          <span className="text-xs font-mono text-gray-500">{order.trackingNo}</span>
                        </div>
                        <button
                          onClick={() => setExpandedId(expandedId === order.id ? null : order.id)}
                          className="text-xs text-red-600 font-semibold cursor-pointer hover:text-red-700 whitespace-nowrap flex items-center gap-1"
                        >
                          {expandedId === order.id ? '收起' : '查看物流'}
                          {expandedId === order.id ? <i className="ri-arrow-up-s-line"></i> : <i className="ri-arrow-down-s-line"></i>}
                        </button>
                      </div>

                      {expandedId === order.id && order.tracking.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <div className="flex flex-col gap-3">
                            {order.tracking.map((t, i) => (
                              <div key={i} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5 ${i === 0 ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                                  {i < order.tracking.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1"></div>}
                                </div>
                                <div className="pb-2">
                                  <p className={`text-xs font-semibold ${i === 0 ? 'text-gray-900' : 'text-gray-500'}`}>{t.desc}</p>
                                  <p className="text-xs text-gray-400 mt-0.5">{t.time} · {t.location}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Address */}
                  <div className="mx-5 mb-4 flex items-start gap-2">
                    <i className="ri-map-pin-line text-gray-400 text-sm mt-0.5 flex-shrink-0"></i>
                    <p className="text-xs text-gray-500">{order.address}</p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-50">
                    {effectiveStatus === 'shipped' && !isConfirmed && (
                      <button
                        onClick={() => handleConfirmReceive(order.id)}
                        className="px-5 py-2 bg-red-600 text-white text-sm font-bold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap"
                      >
                        确认收货
                      </button>
                    )}
                    {(effectiveStatus === 'delivered' || isConfirmed) && (
                      <button className="px-5 py-2 border border-gray-200 text-gray-600 text-sm font-semibold rounded-full cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">
                        评价商品
                      </button>
                    )}
                    {effectiveStatus === 'pending' && (
                      <span className="text-xs text-amber-600 flex items-center gap-1">
                        <i className="ri-time-line"></i>等待商家发货
                      </span>
                    )}
                    <button className="px-4 py-2 text-gray-400 text-sm cursor-pointer hover:text-gray-600 transition-colors whitespace-nowrap">
                      联系客服
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-shopping-bag-line text-5xl text-gray-200 mb-4 block"></i>
              <p className="text-gray-400 text-sm">暂无相关订单</p>
              <Link to="/shop" className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-full cursor-pointer hover:bg-red-700 transition-colors whitespace-nowrap">
                去购物
              </Link>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
